// Import crypto polyfill FIRST before any imports that use uuid
import 'react-native-get-random-values';

import {combineReducers, configureStore} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import {useDispatch} from 'react-redux';

// Slices
import {logicSlice} from '../services/agen';
import {authSlice} from '../services/authSlice';
import {chatSlice} from '../services/chatSlice';

// API
import {api} from '../middleware/api';

// Middleware
import {pollingPauseMiddleware} from '../middleware/pollingPause';
import {rtkQueryErrorLogger} from '../middleware/errorHandler';
import {clearReportSessionStorageMiddleware} from '../middleware/storageCleanup';

// Persistence
import {loadLastState, saveState} from './persist';

// Load the preloaded state
const preloadedState = loadLastState();

/**
 * Root reducer combining all feature reducers
 */
const rootReducer = combineReducers({
  gdata: authSlice.reducer,
  glogic: logicSlice.reducer,
  chat: chatSlice.reducer,
  [api.reducerPath]: api.reducer,
});

/**
 * Infer the RootState type from the root reducer
 * This avoids circular dependencies
 */
export type RootState = ReturnType<typeof rootReducer>;

/**
 * Configure and create the Redux store
 * Following Redux Toolkit best practices:
 * - Uses configureStore for simplified setup
 * - Automatically includes thunk middleware
 * - Enables Redux DevTools in development
 * - Properly types middleware array
 */
export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      // Disable serializable check for RTK Query
      serializableCheck: {
        ignoredActions: [
          // Ignore RTK Query actions
          'api/executeQuery',
          'api/executeMutation',
        ],
      },
    }).concat(
      clearReportSessionStorageMiddleware,
      pollingPauseMiddleware,
      rtkQueryErrorLogger,
      api.middleware,
    ),
  preloadedState,
  // Enable Redux DevTools in development
  devTools: __DEV__,
});

// Subscribe to store changes and persist auth state
store.subscribe(() => {
  saveState(store.getState());
});

// Setup RTK Query listeners for refetchOnFocus/refetchOnReconnect
setupListeners(store.dispatch);

// Export types for use throughout the app
export type AppDispatch = typeof store.dispatch;

/**
 * Typed version of useDispatch hook
 * Use this instead of useDispatch from react-redux for better TypeScript support
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();

export default store;

